import { FC, useRef, useEffect, useCallback } from 'react'
import Portal from '@reach/portal'
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import FocusTrap from 'helpers/focus-trap'
import cn from 'classnames'
import s from './modal.module.css'
import type { ModalProps } from '.'

const Modal: FC<ModalProps> = ({ children, open, onClose, showCloseButton = true, className }) => {
  const ref = useRef() as React.MutableRefObject<HTMLDivElement>

  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose],
  )

  useEffect(() => {
    if (ref.current) {
      if (open) {
        disableBodyScroll(ref.current)
        window.addEventListener('keydown', handleKey)
      } else {
        enableBodyScroll(ref.current)
      }
    }
    return () => {
      window.removeEventListener('keydown', handleKey)
      clearAllBodyScrollLocks()
    }
  }, [open, handleKey])

  return (
    <Portal>
      {open ? (
        <div className={s.root}>
          <div className={cn(s.modal, className)} role="dialog" ref={ref}>
            {showCloseButton && (
              <button type="button" onClick={() => onClose()} aria-label="Close panel" className={s.close}>
                &times;
              </button>
            )}

            <FocusTrap focusFirst>{children}</FocusTrap>
          </div>
        </div>
      ) : null}
    </Portal>
  )
}

Modal.defaultProps = {
  onEnter: () => null,
}

export default Modal
